/* eslint-disable react-hooks/set-state-in-effect */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, object = auth
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Periodic refresh — the grade & unlocked_regions may change server-side
  // (auto-computed on progression). Refresh every 10s while the tab is active so
  // the top-bar grade badge stays in sync with the actual server state.
  useEffect(() => {
    if (!user || user === false) return;
    const t = setInterval(() => {
      if (!document.hidden) refresh();
    }, 10_000);
    return () => clearInterval(t);
  }, [user, refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("pp_token", data.token);
    setUser(data);
    // If the professor was offline > 2h, trainers were paused. Welcome them back.
    if (data.was_paused) {
      toast.success("Bienvenue de retour ! Vos dresseurs reprennent leurs missions.");
    }
    // Re-fetch full /auth/me so we pick up is_admin and other server-only fields.
    refresh();
    return data;
  };

  const register = async (email, password, professor_name) => {
    const { data } = await api.post("/auth/register", {
      email, password, professor_name,
    });
    localStorage.setItem("pp_token", data.token);
    setUser(data);
    refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("pp_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
